/**
 * Author: Fleming Shi
 * Project: DAL (Digital Abstraction Layer)
 * Date: 2025-11-08
 * Revision: 1.0
 * Description: MSAL authentication service for Microsoft Entra ID integration
 */

import { PublicClientApplication } from '@azure/msal-browser'
import { msalConfig, loginRequest } from '@config/authConfig'

// Create a singleton MSAL instance
let msalInstance: PublicClientApplication | null = null
let initializationPromise: Promise<PublicClientApplication> | null = null

export const getMsalInstance = async (): Promise<PublicClientApplication> => {
  // Return existing initialization promise if already in progress
  if (initializationPromise) {
    return initializationPromise
  }

  if (!msalInstance) {
    initializationPromise = (async () => {
      // Validate configuration before creating instance
      if (!msalConfig.auth.clientId || !msalConfig.auth.authority) {
        const error = new Error(
          'MSAL configuration is incomplete. Missing clientId or authority. ' +
            'Please check your .env file and restart the dev server.',
        )
        console.error('MSAL Config Error:', {
          clientId: msalConfig.auth.clientId ? 'present' : 'MISSING',
          authority: msalConfig.auth.authority ? 'present' : 'MISSING',
        })
        throw error
      }

      msalInstance = new PublicClientApplication(msalConfig)
      await msalInstance.initialize()
      return msalInstance
    })()

    return initializationPromise
  }

  return msalInstance
}

export const getAccessToken = async (): Promise<string | null> => {
  try {
    const msal = await getMsalInstance()
    const accounts = msal.getAllAccounts()

    if (accounts.length === 0) {
      // No account, trigger login redirect
      await msal.loginRedirect(loginRequest)
      return null // Will not reach here
    }

    const account = accounts[0]

    try {
      // Try to get token silently
      const response = await msal.acquireTokenSilent({
        ...loginRequest,
        account: account,
      })
      return response.accessToken
    } catch (error) {
      console.error('Silent token acquisition failed:', error)
      // If silent fails, trigger login redirect
      await msal.loginRedirect(loginRequest)
      return null // Will not reach here
    }
  } catch (error) {
    console.error('Error getting access token:', error)
    return null
  }
}
